class NewsletterPopup {
  constructor() {
    this.section = document.querySelector('.newsletter-popup[data-section-id]');
    if (!this.section) return;

    this.enabled = this.section.dataset.enabled === 'true';
    this.delay = parseInt(this.section.dataset.delay, 10) * 1000 || 5000;

    this.storageKeys = {
      dismissed: 'ttm-newsletter-dismissed',
      floatingHidden: 'ttm-newsletter-floating-hidden',
      step: 'ttm-newsletter-step',
      style: 'ttm-newsletter-style'
    };

    this.elements = {
      overlay: this.section.querySelector('.newsletter-popup__overlay'),
      modal: this.section.querySelector('.newsletter-popup__modal'),
      content: this.section.querySelector('.newsletter-popup__content'),
      declineLinks: this.section.querySelectorAll('[data-action="decline"]'),
      backBtn: this.section.querySelector('[data-action="prev-step"]'),
      floatingBtn: document.querySelector('.newsletter-popup__floating-btn'),
      styleBtns: this.section.querySelectorAll('.newsletter-popup__style-btn'),
      step1: this.section.querySelector('[data-step="1"]'),
      step2: this.section.querySelector('[data-step="2"]'),
      form: this.section.querySelector('.newsletter-popup__form'),
      emailInput: this.section.querySelector('#newsletter-popup-email'),
      submitBtn: this.section.querySelector('.newsletter-popup__submit'),
      successMarker: this.section.querySelector('#newsletter-popup-success')
    };

    this.state = {
      step: 1,
      selectedStyle: null,
      isOpen: false
    };

    this.init();
  }

  init() {
    if (!this.enabled) return;

    this.restoreState();
    this.bindEvents();

    // The page was just reloaded by Shopify's native captcha submit after a
    // successful signup (form.posted_successfully? rendered the success marker).
    // Open the popup directly and show the thank-you message, regardless of any
    // previously stored dismissed state.
    if (this.successMarker) {
      this.clearSessionState();
      this.open(true);
      this.showServerSuccess();
      return;
    }

    if (!this.isDismissed() && !this.state.isOpen) {
      this.scheduleOpen();
    } else if (this.isDismissed() && !this.isFloatingHidden()) {
      this.showFloatingButton();
    }
  }

  restoreState() {
    try {
      const storedStep = sessionStorage.getItem(this.storageKeys.step);
      const storedStyle = sessionStorage.getItem(this.storageKeys.style);

      if (storedStep) {
        this.state.step = parseInt(storedStep, 10);
      }
      if (storedStyle) {
        this.state.selectedStyle = storedStyle;
      }
    } catch (e) {
      console.warn('[NewsletterPopup] sessionStorage unavailable', e);
    }

    this.applyStepState();
  }

  isDismissed() {
    try {
      return localStorage.getItem(this.storageKeys.dismissed) === 'true';
    } catch (e) {
      return false;
    }
  }

  isFloatingHidden() {
    try {
      return localStorage.getItem(this.storageKeys.floatingHidden) === 'true';
    } catch (e) {
      return false;
    }
  }

  setFloatingHidden(value) {
    try {
      localStorage.setItem(this.storageKeys.floatingHidden, value.toString());
    } catch (e) {
      console.warn('[NewsletterPopup] localStorage unavailable', e);
    }
  }

  setDismissed(value) {
    try {
      localStorage.setItem(this.storageKeys.dismissed, value.toString());
    } catch (e) {
      console.warn('[NewsletterPopup] localStorage unavailable', e);
    }
  }

  saveStep(step) {
    this.state.step = step;
    try {
      sessionStorage.setItem(this.storageKeys.step, step.toString());
    } catch (e) {
      console.warn('[NewsletterPopup] sessionStorage unavailable', e);
    }
    this.applyStepState();
  }

  saveStyle(style) {
    this.state.selectedStyle = style;
    try {
      sessionStorage.setItem(this.storageKeys.style, style);
    } catch (e) {
      console.warn('[NewsletterPopup] sessionStorage unavailable', e);
    }
  }

  clearSessionState() {
    try {
      sessionStorage.removeItem(this.storageKeys.step);
      sessionStorage.removeItem(this.storageKeys.style);
    } catch (e) {
      console.warn('[NewsletterPopup] sessionStorage unavailable', e);
    }
    this.state.step = 1;
    this.state.selectedStyle = null;
  }

  applyStepState() {
    if (this.state.step === 1) {
      this.showStep1();
    } else {
      this.showStep2();
    }
  }

  showStep1() {
    if (this.elements.step1) {
      this.elements.step1.classList.remove('is-hidden');
    }
    if (this.elements.step2) {
      this.elements.step2.classList.remove('is-active');
    }
  }

  showStep2() {
    if (this.elements.step1) {
      this.elements.step1.classList.add('is-hidden');
    }
    if (this.elements.step2) {
      this.elements.step2.classList.add('is-active');
    }
    this.elements.emailInput?.focus();
  }

  scheduleOpen() {
    if (this.openTimeout) clearTimeout(this.openTimeout);
    this.openTimeout = setTimeout(() => this.open(), this.delay);
  }

  open(force = false) {
    if (!force && (this.isDismissed() || this.state.isOpen)) return;

    this.resetPopupContent();

    this.state.isOpen = true;
    this.section.classList.add('newsletter-popup--active');
    this.section.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');

    this.trapFocus(this.elements.content);
    this.elements.content?.focus();

    this.applyStepState();
  }

  resetPopupContent() {
    if (this.successShown) {
      this.successShown = false;
      if (this.successElement) {
        this.successElement.remove();
        this.successElement = null;
      }
      if (this.dismissTimeout) {
        clearTimeout(this.dismissTimeout);
        this.dismissTimeout = null;
      }
      this.state.step = 1;
      this.state.selectedStyle = null;
      this.elements.step1?.classList.remove('is-hidden');
      this.elements.step2?.classList.remove('is-active');
      if (this.elements.emailInput) {
        this.elements.emailInput.value = '';
      }
    }
  }

  close() {
    if (!this.state.isOpen) return;

    this.state.isOpen = false;
    this.section.classList.remove('newsletter-popup--active');
    this.section.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');

    this.removeTrapFocus();
    this.showFloatingButton();
  }

  dismiss() {
    this.setDismissed(true);
    this.close();
    this.clearSessionState();
  }

  dismissFloating() {
    this.setDismissed(true);
    this.setFloatingHidden(true);
    this.hideFloatingButton();
  }

  showFloatingButton() {
    if (this.isFloatingHidden()) return;
    const btn = this.elements.floatingBtn;
    if (btn) {
      btn.hidden = false;
      requestAnimationFrame(() => {
        btn.classList.add('newsletter-popup__floating-btn--visible');
      });
    }
  }

  hideFloatingButton() {
    const btn = this.elements.floatingBtn;
    if (btn) {
      btn.classList.remove('newsletter-popup__floating-btn--visible');
      setTimeout(() => {
        btn.hidden = true;
      }, 300);
    }
  }

  markSignupComplete() {
    this.setDismissed(true);
    this.setFloatingHidden(true);
    this.hideFloatingButton();
  }

  nextStep() {
    if (this.state.step === 1 && this.state.selectedStyle) {
      this.saveStep(2);
    }
  }

  prevStep() {
    if (this.state.step === 2) {
      this.saveStep(1);
    }
  }

  selectStyle(style) {
    this.saveStyle(style);
    this.elements.styleBtns.forEach(btn => {
      const isSelected = btn.dataset.style === style;
      btn.setAttribute('aria-checked', isSelected.toString());
    });
    setTimeout(() => this.nextStep(), 150);
  }

  async handleSubmit(event) {
    const email = this.elements.emailInput?.value?.trim();
    if (!email || !this.validateEmail(email)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.elements.emailInput?.focus();
      return;
    }

    const form = this.elements.form;

    // Always include the current style tag before the form leaves our control.
    const tagsInput = this.section.querySelector('#newsletter-popup-tags');
    const styleTag = this.state.selectedStyle
      ? `, newsletter-${this.state.selectedStyle}`
      : '';
    if (tagsInput) {
      tagsInput.value = `shopify-forms-1028238${styleTag}`;
    }

    // Shopify's captcha bootstrap injects a fresh `form_key` into
    // contact/customer/newsletter forms on every native submit. Replicate that
    // here so each path we control (AJAX and the protected native fallback)
    // carries a valid form_key and Shopify does not reject the request.
    let formKey = form.elements['form_key'];
    if (!formKey) {
      const keyInput = document.createElement('input');
      keyInput.type = 'hidden';
      keyInput.name = 'form_key';
      form.appendChild(keyInput);
      formKey = form.elements['form_key'];
    }
    formKey.value = Array.from(Array(32), () => Math.random().toString(36)[2]).join('');

    // If Shopify's captcha is already wired up to this form, let the provider
    // own the submit flow: it generates the hCaptcha token and performs the
    // native submit (page reload). Interfering here would break token
    // generation and Shopify would reject the submission.
    if (form.dataset.hcaptchaBound === 'true' || form.dataset.recaptchaBound === 'true') {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitBtn = this.elements.submitBtn;
    const submitText = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = '...';

    try {
      const res = await fetch(form.action, { method: 'POST', body: new FormData(form) });
      if (!res.ok) {
        // Shopify rejected the AJAX request (e.g. a valid hCaptcha token is
        // required but was not sent). Force Shopify's captcha wire-up, then let
        // the provider generate a valid token and perform the native submit
        // (page reload is expected; the popup shows the thank-you via the
        // server-rendered success marker).
        submitBtn.textContent = submitText;
        if (window.Shopify && typeof window.Shopify.captcha.protect === 'function') {
          window.Shopify.captcha.protect(form, () => form.submit());
        } else {
          form.submit();
        }
        return;
      }

      this.showSuccess();
    } catch (error) {
      console.error('[NewsletterPopup] Submit error', error);
      submitBtn.disabled = false;
      submitBtn.textContent = submitText;
    }
  }

  validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  createSuccessElement() {
    const formArea = this.section.querySelector('.newsletter-popup__form-area');
    if (!formArea) return null;

    if (this.successElement) return this.successElement;

    const success = document.createElement('div');
    success.className = 'newsletter-popup__success';
    success.setAttribute('role', 'status');
    success.setAttribute('tabindex', '-1');
    success.innerHTML = `
      <svg class="newsletter-popup__success-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
        <path d="M8 12l2 2 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <h3 class="newsletter-popup__success-title">Danke für deine Anmeldung!</h3>
      <p class="newsletter-popup__success-text">Dein 10 % Rabattcode wird dir per E-Mail zugeschickt.</p>
    `;
    formArea.appendChild(success);
    this.successElement = success;
    return success;
  }

  showSuccess() {
    if (this.dismissTimeout) clearTimeout(this.dismissTimeout);

    this.successShown = true;
    const success = this.createSuccessElement();
    if (!success) return;

    this.elements.step1?.classList.add('is-hidden');
    this.elements.step2?.classList.remove('is-active');

    this.markSignupComplete();
    this.dismissTimeout = setTimeout(() => this.dismiss(), 2000);
  }

  showServerSuccess() {
    this.successShown = true;
    const success = this.createSuccessElement();
    if (!success) return;

    this.elements.step1?.classList.add('is-hidden');
    this.elements.step2?.classList.remove('is-active');
    this.markSignupComplete();
    success.focus();
  }

  bindEvents() {
    this.elements.declineLinks?.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.dismiss();
      });
    });
    this.elements.backBtn?.addEventListener('click', () => this.prevStep());
    this.elements.floatingBtn?.addEventListener('click', () => {
      if (window.matchMedia('(max-width: 749px)').matches) {
        this.dismissFloating();
      } else {
        this.hideFloatingButton();
        this.setDismissed(false);
        this.open();
      }
    });
    this.elements.overlay?.addEventListener('click', () => this.dismiss());

    this.elements.styleBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const style = btn.dataset.style;
        if (style) this.selectStyle(style);
      });
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const style = btn.dataset.style;
          if (style) this.selectStyle(style);
        }
      });
    });

    this.elements.form?.addEventListener('submit', (e) => this.handleSubmit(e));

    this.elements.emailInput?.addEventListener('input', () => {
      if (this.elements.emailInput.checkValidity()) {
        this.elements.submitBtn.disabled = false;
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.state.isOpen) {
        this.dismiss();
      }
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    }
  }

  trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    this._trapFocusHandler = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', this._trapFocusHandler);
    firstElement?.focus();
  }

  removeTrapFocus() {
    if (this._trapFocusHandler) {
      document.removeEventListener('keydown', this._trapFocusHandler);
      this._trapFocusHandler = null;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.newsletterPopup = new NewsletterPopup();
});

if (typeof module !== 'undefined' && module.exports) {
  module.exports = NewsletterPopup;
}